import React, { useState, useEffect, useRef } from "react";
import jsQR from "jsqr";
import "./QRScanner.css";

const decodeQRString = (qrText) => {
  if (!qrText || typeof qrText !== "string") {
    return { success: false, error: "Empty QR code." };
  }

  if (qrText.startsWith("NIMBLEBASH://checkin/")) {
    const base64Data = qrText.slice("NIMBLEBASH://checkin/".length);

    if (!base64Data) {
      return { success: false, error: "QR code data is empty." };
    }

    let binaryString;
    try {
      binaryString = atob(base64Data);
    } catch {
      return { success: false, error: "Unable to decode QR code." };
    }

    let jsonString;
    try {
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      jsonString = new TextDecoder("utf-8").decode(bytes);
    } catch {
      jsonString = binaryString;
    }

    let payload;
    try {
      payload = JSON.parse(jsonString);
    } catch {
      return { success: false, error: "QR code contains invalid data." };
    }

    if (!payload.eventId || !payload.guestId) {
      return {
        success: false,
        error: "QR code is missing required information.",
      };
    }

    return { success: true, payload };
  }

  try {
    const payload = JSON.parse(qrText);
    if (payload && payload.eventId && payload.guestId) {
      return { success: true, payload };
    }
    return {
      success: false,
      error: "Invalid QR code format. Please scan a valid guest ticket.",
    };
  } catch {
    // Not JSON and not NIMBLEBASH deep-link
    return {
      success: false,
      error: "Invalid QR code format. Please scan a valid guest ticket.",
    };
  }
};

const QRScanner = ({ isOpen, onClose, onScanSuccess, eventId }) => {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastScannedRef = useRef("");

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Start camera
  useEffect(() => {
    if (!isOpen || !videoRef.current) return;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });

        streamRef.current = stream;
        videoRef.current.srcObject = stream;

        await videoRef.current.play();

        setScanning(true);
        setError(null);
        setCameraPermissionDenied(false);

        tick();

        // console.log("✅ Camera started successfully");
      } catch (err) {
        // console.error("❌ Error starting camera:", err);

        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setCameraPermissionDenied(true);
          setError(
            "Camera permission denied. Please allow camera access in your browser settings."
          );
        } else if (err.name === "NotFoundError") {
          setError("No camera found on this device.");
        } else {
          setError(`Failed to start camera: ${err.message}`);
        }
      }
    };

    startCamera();

    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const tick = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (code && code.data) {
        if (lastScannedRef.current === code.data) {
          animationFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        lastScannedRef.current = code.data;
        handleQRDetected(code.data);
        return;
      }
    }

    animationFrameRef.current = requestAnimationFrame(tick);
  };

  const handleQRDetected = (qrText) => {
    setScanning(false);

    try {
      const decodeResult = decodeQRString(qrText);

      if (!decodeResult.success) {
        setError(decodeResult.error);
        scheduleReset();
        return;
      }

      const { payload } = decodeResult;

      if (payload.eventId !== eventId) {
        setError("This ticket is for a different event.");
        scheduleReset();
        return;
      }

      stopCamera();
      onScanSuccess(payload);
    } catch (err) {
      setError("Failed to process QR code. Please try again.");
      console.error("QR processing error:", err);
      scheduleReset();
    }
  };

  const scheduleReset = () => {
    setTimeout(() => {
      setError(null);
      setScanning(true);
      lastScannedRef.current = "";
      tick();
    }, 3000);
  };

  const stopCamera = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setScanning(false);
  };

  const handleClose = () => {
    stopCamera();
    setError(null);
    setCameraPermissionDenied(false);
    lastScannedRef.current = "";
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    setCameraPermissionDenied(false);
    lastScannedRef.current = "";
    window.location.reload();
  };

  if (!isOpen) return null;

  if (!isMobile) {
    return (
      <div className="qr-scanner-mobile-overlay">
        <div className="qr-scanner-mobile-container">
          <div className="qr-scanner-mobile-header">
            <button
              className="qr-scanner-mobile-header-btn"
              onClick={handleClose}
              aria-label="Close scanner"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <h2 className="qr-scanner-mobile-title">Scan Guest Ticket</h2>
            <div
              className="qr-scanner-mobile-header-btn"
              style={{ visibility: "hidden" }}
            ></div>
          </div>

          <div className="qr-scanner-desktop-message">
            <div className="qr-scanner-desktop-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"
                  fill="#00FF00"
                />
              </svg>
            </div>
            <h3 className="qr-scanner-desktop-message-title">
              Use Your Mobile Device
            </h3>
            <p className="qr-scanner-desktop-message-text">
              QR code scanning works best on mobile devices with a camera.
            </p>
            <p className="qr-scanner-desktop-message-text">
              Please open this page on your smartphone or tablet to scan guest
              tickets.
            </p>

            <div className="qr-scanner-desktop-steps">
              <div className="qr-scanner-desktop-step">
                <span className="qr-scanner-desktop-step-number">1</span>
                <p>Open NimbleBash on your mobile device</p>
              </div>
              <div className="qr-scanner-desktop-step">
                <span className="qr-scanner-desktop-step-number">2</span>
                <p>Navigate to this event</p>
              </div>
              <div className="qr-scanner-desktop-step">
                <span className="qr-scanner-desktop-step-number">3</span>
                <p>Tap "Check-In Guest" to scan tickets</p>
              </div>
            </div>
          </div>

          <div className="qr-scanner-mobile-instructions">
            <button
              className="qr-scanner-desktop-close-btn"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-scanner-mobile-overlay">
      <div className="qr-scanner-mobile-container">
        <div className="qr-scanner-mobile-header">
          <button
            className="qr-scanner-mobile-header-btn"
            onClick={handleClose}
            aria-label="Close scanner"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill="currentColor"
              />
            </svg>
          </button>
          <h2 className="qr-scanner-mobile-title">Scan Guest Ticket</h2>
          <div
            className="qr-scanner-mobile-header-btn"
            style={{ visibility: "hidden" }}
          ></div>
        </div>

        {!cameraPermissionDenied && !error && (
          <div className="qr-scanner-mobile-scanning-area">
            <div className="qr-scanner-mobile-frame">
              <div className="qr-scanner-mobile-corner qr-scanner-mobile-corner-tl"></div>
              <div className="qr-scanner-mobile-corner qr-scanner-mobile-corner-tr"></div>
              <div className="qr-scanner-mobile-corner qr-scanner-mobile-corner-bl"></div>
              <div className="qr-scanner-mobile-corner qr-scanner-mobile-corner-br"></div>

              {scanning && <div className="qr-scanner-mobile-scan-line"></div>}
            </div>
          </div>
        )}

        <div className="qr-scanner-mobile-camera-wrapper">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>

        <div className="qr-scanner-mobile-instructions">
          {error ? (
            <div className="qr-scanner-mobile-error">
              <div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    fill="currentColor"
                  />
                </svg>
                <span>{error}</span>
              </div>
              {!cameraPermissionDenied && (
                <button onClick={handleRetry} className="qr-scanner-retry-btn">
                  Try Again
                </button>
              )}
            </div>
          ) : (
            <p className="qr-scanner-mobile-instruction-text">
              {scanning
                ? "Position the QR code within the frame"
                : "Initializing camera..."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
